import {
  ApolloClient,
  ApolloLink,
  DefaultOptions,
  DocumentNode,
  Operation,
  createHttpLink,
  from,
} from "@apollo/client";
import { NetworkError } from "@apollo/client/errors";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getOperationName } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { GraphQLFormattedError } from "graphql";
import { uniq } from "lodash";

import cache, {
  appVersionVar,
  editorVersionVar,
  errorNotificationVar,
} from "@cache/cache";
import {
  ErrorMatchType,
  defaultNetworkErrorHandler,
  findErrorMatch,
  unexpectedGraphqlErrors,
} from "@components/use-error/use-error";
import { getPusherClient } from "@helpers/pusher";

// Client
const customFetch = (uri: RequestInfo | URL, options: any) => {
  const { operationName } = JSON.parse(options.body);
  return fetch(`${uri}?opname=${operationName}`, options);
};

const customBatchFetch = (uri: RequestInfo | URL, options: any) => {
  const body = JSON.parse(options.body);
  const operationName = uniq(
    body.map(({ operationName }: { operationName: string }) => operationName),
  ).join();
  return fetch(`${uri}?opname=${operationName}`, options);
};

const httpLink = createHttpLink({
  uri: `/graphql`,
  fetch: customFetch,
});

const publicHttpLink = createHttpLink({
  uri: `/public_graphql/`,
  fetch: customFetch,
});

const batchHttpLink = new BatchHttpLink({
  uri: `/graphql`,
  fetch: customBatchFetch,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = window.TF.csrf;
  const pusher = getPusherClient();
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      [`X-CSRFToken`]: token,
      // https://pusher.com/docs/channels/server_api/excluding-event-recipients/
      // we send this socket id to prevent pusher events to received by user emitting the event.
      [`X-pusher-socket-id`]: pusher?.connection.socket_id,
    },
  };
});

// Convert the value to a number if it is the id field and it is actually a number
const convertIdToInteger = (key: string, value: any) => {
  return key === "id" ? (isNaN(value) ? value : parseInt(value)) : value;
};

const convertIdToIntegerLink = new ApolloLink((operation, forward) => {
  const newOperation = operation;
  return forward(newOperation).map((data) => {
    return JSON.parse(JSON.stringify(data), convertIdToInteger);
  });
});

const appVersionLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    // set app version
    const appVersion = operation
      .getContext()
      .response.headers.get("X-App-Version");
    appVersionVar(appVersion);

    // set editor version
    const newVersion = operation
      .getContext()
      .response.headers.get("X-Editor-Extensions-Version");
    if (newVersion) {
      const currentEditorVersion = editorVersionVar();
      editorVersionVar({
        ...currentEditorVersion,
        oldVersion: currentEditorVersion.oldVersion
          ? currentEditorVersion.oldVersion
          : newVersion,
        newVersion,
      });
    }
    return response;
  });
});

// The backend message is part of the Sentry issue name, so rewording a backend
// GraphQLError starts a new Sentry issue. An `errorMatches` entry gives the toast
// friendlier text, but a matched error is also no longer reported to Sentry.
function formattedErrorToError(
  formattedError: GraphQLFormattedError,
  operation: Operation,
): Error {
  const error = new Error(formattedError.message);
  (error as any).name =
    `[GraphQL Error] ${operation.operationName}: ${formattedError.message}`;
  (error as any).path = formattedError.path;
  (error as any).locations = formattedError.locations;
  (error as any).extensions = formattedError.extensions;
  return error;
}

/** Shows the toast and reports to Sentry. Returns how many events it sent. */
function reportGraphqlErrors(
  graphQLErrors: ReadonlyArray<GraphQLFormattedError>,
  operation: Operation,
  { suppressToast }: { suppressToast: boolean },
): number {
  const errorMatches = (operation.getContext().errorMatches ||
    []) as ErrorMatchType[];

  const firstGraphqlError = graphQLErrors[0];
  const errorMatch = findErrorMatch(firstGraphqlError.message, errorMatches);

  if (!suppressToast) {
    errorNotificationVar(
      errorMatch || {
        title: "An unexpected error occurred.",
        description: (
          <>
            <div>We have been notified. Please try again.</div>
            {firstGraphqlError?.message && (
              <div className="text-xs mt-4 tracking-tight pl-2 border-l-2 border-gray-100 text-gray-400">
                {firstGraphqlError.message}
              </div>
            )}
          </>
        ),
      },
    );
  }

  const unexpected = unexpectedGraphqlErrors(graphQLErrors, errorMatches);

  unexpected.forEach((graphQLError) => {
    Sentry.captureException(formattedErrorToError(graphQLError, operation), {
      extra: {
        operationName: operation.operationName,
        variables: operation.variables,
        error: graphQLError,
      },
    });
  });

  return unexpected.length;
}

/** A failure of the server itself, rather than a rejected request. */
function isServerFailure(
  networkError: NetworkError | undefined,
): networkError is NetworkError {
  return Boolean(
    networkError &&
      "statusCode" in networkError &&
      networkError.statusCode >= 500,
  );
}

// Reports errors and shows a toast. The `: void` return type is load-bearing:
// in an `onError` link a returned observable means "retry", which would send
// every failing operation a second time (TF-1686).
export const sentryLink = onError(
  ({ graphQLErrors, networkError, operation }): void => {
    // Set by the callers that render this operation's error inline. It hides
    // the toast only. Sentry still hears about the failure, and so do the
    // session-expired, stale-CSRF and offline prompts, which are conditions of
    // the app rather than of one operation (TF-1687).
    const suppressToast = Boolean(
      operation.getContext().skipGraphqlErrorHandling,
    );

    // A transport failure that carries an `errors` body sets BOTH
    // `graphQLErrors` and `networkError`. Report it once, as the GraphQL error
    // it really is: running both branches sent a second, generic Sentry event
    // and replaced the toast that named the real problem (TF-1687).
    //
    // `graphQLErrors` comes straight from the response body, so a malformed one
    // must not throw. An exception here escapes before Apollo calls
    // `observer.error`, and the operation never settles.
    const reportable = Array.isArray(graphQLErrors)
      ? graphQLErrors.filter((error) => typeof error?.message === "string")
      : [];

    if (reportable.length > 0) {
      const reported = reportGraphqlErrors(reportable, operation, {
        suppressToast,
      });

      // `errorMatches` silences expected validation errors. It must not
      // silence a server failure that happens to carry one of those messages,
      // or the 5xx would reach nobody. The toast stays the GraphQL one.
      if (reported === 0 && isServerFailure(networkError)) {
        defaultNetworkErrorHandler(networkError, operation, {
          suppressGenericToast: true,
        });
      }
      return;
    }

    if (networkError) {
      defaultNetworkErrorHandler(networkError, operation, {
        suppressGenericToast: suppressToast,
      });
    }
  },
);

const clientDefaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "cache-and-network",
  },
};
const client = new ApolloClient({
  link: from([
    sentryLink,
    authLink,
    convertIdToIntegerLink,
    appVersionLink,
    httpLink,
  ]),
  cache,
  defaultOptions: clientDefaultOptions,
});

export const publicClient = new ApolloClient({
  link: from([
    sentryLink,
    authLink,
    convertIdToIntegerLink,
    appVersionLink,
    publicHttpLink,
  ]),
  cache,
  defaultOptions: clientDefaultOptions,
});

export const batchClient = new ApolloClient({
  link: from([
    sentryLink,
    authLink,
    convertIdToIntegerLink,
    appVersionLink,
    batchHttpLink,
  ]),
  cache,
  defaultOptions: clientDefaultOptions,
});

export const getWatchedQuery = (query: any) => {
  const queries = Array.from(client.getObservableQueries().entries()).map(
    (arr) => arr[1],
  );

  return queries.find(({ queryName }) => queryName === getOperationName(query));
};

export const getWatchedQueries = (query: DocumentNode) => {
  const nonBatchQueries = Array.from(
    client.getObservableQueries().entries(),
  ).map((arr) => arr[1]);
  const batchQueries = Array.from(
    batchClient.getObservableQueries().entries(),
  ).map((arr) => arr[1]);
  const queries = [...nonBatchQueries, ...batchQueries];
  return queries.filter(
    ({ queryName }) => queryName === getOperationName(query),
  );
};

export default client;
